import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  Collapse,
  Divider,
  Modal,
  type CardAdvanceRowProps,
  type ModalRef,
} from "../../components";
import {
  TpaAdditionalServiceApprovalStatus,
  TpaApprovalStatus,
  type AdditionalChargeRequest,
  type TpaAdditionalServicesActionPayload,
} from "../../apis/order";
import { Tools } from "../../utils";
import type { BookingDetailCharges } from "./viewModel";
import { OrderStatusChip } from "../ManageBookings/orderStatusChip";

export type AdditionalChargesCardProps = {
  charges: BookingDetailCharges;
  submitApproval: (
    status: TpaAdditionalServiceApprovalStatus,
    payload?: TpaAdditionalServicesActionPayload,
  ) => Promise<void>;
};

type ChargeItem = CardAdvanceRowProps | React.ReactElement;

function getRequestChargeItems(
  request: AdditionalChargeRequest,
  t: (key: string, options?: { rate: number }) => string,
): ChargeItem[] {
  const { lineItems, totals } = request;

  return [
    ...lineItems.map((item) => ({
      value: item.label,
      extra: Tools.formatEur(item.amount),
    })),
    {
      value: t("manageBookings.detail.cardTransactionFee"),
      extra: Tools.formatEur(totals.trxFee),
    },
    {
      value: t("manageBookings.detail.transactionFeeVat", {
        rate: totals.trxFeeTaxPercentage ?? 0,
      }),
      extra: Tools.formatEur(totals.trxFeeTax),
    },
    <Divider />,
    {
      value: t("manageBookings.detail.subtotal"),
      extra: Tools.formatEur(totals.payableAmount),
    },
    {
      value: t("manageBookings.detail.paidApprovedAmount"),
      extra: Tools.formatEur(totals.paidAmount),
    },
    {
      value: t("manageBookings.detail.outstandingAmountDue"),
      extra: Tools.formatEur(totals.outstandingAmount),
    },
  ];
}

export function AdditionalChargesCard({
  charges,
  submitApproval,
}: AdditionalChargesCardProps) {
  const { t } = useTranslation();
  const acceptModalRef = React.useRef<ModalRef | null>(null);
  const rejectModalRef = React.useRef<ModalRef | null>(null);
  const [activeApprovalRequestId, setActiveApprovalRequestId] = React.useState<
    string | null
  >(null);

  const { additionalChargeRequests } = charges;
  const [activeKey, setActiveKey] = React.useState<any>();

  React.useEffect(() => {
    const id = additionalChargeRequests.find(
      (request) => request.status === TpaApprovalStatus.pendingTpaApproval,
    )?.approvalRequestId;

    setActiveKey(id);
  }, [additionalChargeRequests]);

  function renderAdditionalChargePanelContent({
    request,
  }: {
    request: AdditionalChargeRequest;
  }) {
    return (
      <>
        <div className="grid gap-[16px]">
          {getRequestChargeItems(request, t).map((item, index) =>
            React.isValidElement(item) ? (
              <React.Fragment key={index} children={item} />
            ) : (
              <Card.AdvanceRow key={index} {...item} />
            ),
          )}
        </div>
        {request.showApprovalActions && (
          <div className="mt-[16px]">
            <Divider />
            <div className="flex gap-[12px] my-[16px]">
              <Button
                className="flex-1"
                color="secondaryDark"
                onClick={() => {
                  setActiveApprovalRequestId(request.approvalRequestId);
                  acceptModalRef.current?.open();
                }}
              >
                {t("general.accept")}
              </Button>
              <Button
                className="flex-1"
                color="secondary"
                onClick={() => {
                  setActiveApprovalRequestId(request.approvalRequestId);
                  rejectModalRef.current?.open();
                }}
              >
                {t("general.reject")}
              </Button>
            </div>
          </div>
        )}
      </>
    );
  }

  const approvalRequestPayload = (
    approvalRequestId: string | null,
  ): TpaAdditionalServicesActionPayload | undefined =>
    approvalRequestId ? { approvalRequestId } : undefined;

  return (
    <>
      <Collapse
        accordion
        activeKey={activeKey}
        onChange={setActiveKey}
        title={t("manageBookings.detail.additionalCharges")}
        items={additionalChargeRequests.map((request) => ({
          key: request.approvalRequestId,
          extra: !!request.status && (
            <OrderStatusChip status={request.status} />
          ),
          label: request.title,
          children: renderAdditionalChargePanelContent({ request }),
        }))}
      />

      <Modal
        ref={acceptModalRef}
        title={t("manageBookings.approval.acceptTitle")}
      >
        <Modal.Confirmation
          description={t("manageBookings.approval.acceptDescription")}
          buttons={[
            {
              children: t("general.cancel"),
              color: "secondary",
              variant: "outline",
              onClick: () => {
                acceptModalRef.current?.close();
                setActiveApprovalRequestId(null);
              },
            },
            {
              children: t("general.accept"),
              color: "secondaryDark",
              onClick: async () => {
                await submitApproval(
                  TpaAdditionalServiceApprovalStatus.approve,
                  approvalRequestPayload(activeApprovalRequestId),
                );
                acceptModalRef.current?.close();
                setActiveApprovalRequestId(null);
              },
            },
          ]}
        />
      </Modal>

      <Modal
        ref={rejectModalRef}
        title={t("manageBookings.approval.rejectTitle")}
      >
        <Modal.Prompt
          description={t("manageBookings.approval.rejectDescription")}
          placeholder={t("manageBookings.approval.reasonPlaceholder")}
          buttons={(text) => [
            {
              children: t("general.cancel"),
              color: "secondary",
              variant: "outline",
              onClick: () => {
                rejectModalRef.current?.close();
                setActiveApprovalRequestId(null);
              },
            },
            {
              children: t("general.reject"),
              color: "critical",
              disabled: !text.trim(),
              onClick: async () => {
                await submitApproval(
                  TpaAdditionalServiceApprovalStatus.reject,
                  {
                    ...approvalRequestPayload(activeApprovalRequestId),
                    reason: text.trim(),
                  },
                );
                rejectModalRef.current?.close();
                setActiveApprovalRequestId(null);
              },
            },
          ]}
        />
      </Modal>
    </>
  );
}
